import { useCallback, useEffect, useState } from 'react'

import { useFetcher } from 'react-router'

import { InfiniteScrollableData } from '~/types/infiniteScroll'
import { addSearchParams } from '~/utils/shared/routing'

interface InfiniteScrollOptions {
  searchParams?: Record<string, string>,
  scrollBoundary?: number,
  initialPageOffset?: number,
}

export const useInfiniteScroll = <T extends { id?: string }>(
  loaderHref: string,
  initialData: T[],
  totalSize: number,
  containerElement: HTMLDivElement | null,
  options?: InfiniteScrollOptions,
): {
  isFetching: boolean,
  data: T[],
  fetchMore: (containerElement?: HTMLDivElement | null) => void,
} => {
  const {
    searchParams,
    scrollBoundary = 300,
    initialPageOffset = 2, // start with 2 because 1 is pre-loaded
  } = options || {}

  const [data, setData] = useState<T[]>(initialData)

  const [page, setPage] = useState(initialPageOffset)
  const [shouldFetch, setShouldFetch] = useState(true)
  const [fetchingStarted, setFetchingStarted] = useState(false)

  useEffect(() => {
    setData(initialData)
    setPage(initialPageOffset)
  }, [initialData])

  const totalFetched = data.length

  const { state, data: fetcherData, load } = useFetcher<InfiniteScrollableData<T>>()
  const { dataPortion } = fetcherData || {}

  const isFetching = state === 'loading'

  const fetchMore = useCallback((containerElement?: HTMLDivElement | null) => {
    if (containerElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerElement
      // once the user has scrolled within given boundary of the bottom of the container, fetch more data if there are any
      if (
        scrollHeight - scrollTop - clientHeight < scrollBoundary &&
        shouldFetch &&
        totalFetched < totalSize
      ) {
        const params = {
          ...searchParams,
          page: String(page),
        }
        load(addSearchParams(loaderHref, params))
        setShouldFetch(false)
        setFetchingStarted(true)
      }
    }
  }, [load, shouldFetch, page, totalFetched, totalSize, scrollBoundary])

  useEffect(() => {
    fetchMore(containerElement)
  }, [])

  useEffect(() => {
    if (fetchingStarted && dataPortion) {
      if (dataPortion.length === 0) {
        setShouldFetch(false)
      } else {
        setData(prevData => [...prevData, ...(dataPortion as T[])])
        setPage(prevPage => prevPage + 1)
        setShouldFetch(true)
      }
      setFetchingStarted(false)
    }
  }, [dataPortion])

  return { isFetching, data, fetchMore }
}